<template>
  <v-col>
    <v-row class="mb-3 mt-0">
      <h2 class="pl-2 my-1 pa-sm-0 primary--text">
        {{ $translate('ORDER_OVERVIEW') }}
      </h2>
    </v-row>

    <BookingProductList></BookingProductList>
    <v-list style="width: 100% !important" class="py-0" dense>
      <template v-for="(ticket, index) in listOfTickets">
        <!-- Order item for Products, VirtualProducts and Rentals -->
        <v-list-item :key="index" v-if="ticket.Amount > 0" dense class="px-2">
          <v-list-item-content>
            <v-card flat color="grey lighten-4" class="px-3">
              <v-row class="my-0">
                <v-col cols="8">
                  <v-list-item-title class="mb-1">
                    {{ ticket.Name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ formatter.format(getTicketUnitPrice(ticket)) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle>
                    {{ $translate('QUANTITY') }}: {{ ticket.Amount }}
                  </v-list-item-subtitle>
                </v-col>
                <v-col cols="4" class="text-right">
                  <v-list-item-title>
                    {{ formatter.format(getTicketTotalPrice(ticket)) }}
                  </v-list-item-title>
                  <span v-if="isProfUser" class="text-caption text--secondary">
                    {{ $translate('EXCL_VAT') }}
                  </span>
                </v-col>
              </v-row>
            </v-card>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>

    <v-row class="my-0">
      <v-spacer></v-spacer>
      <v-col cols="12" md="4" class="text-end">
        <v-row
          v-if="isProfUser"
          class="ma-0 text-subtitle-2"
          justify="space-between"
        >
          <div>{{ $translate('SUBTOTAL') }}</div>
          <div>{{ formatter.format(getSubtotalPrice) }}</div>
        </v-row>
        <v-row
          v-if="isDeliveryOrder"
          class="ma-0 text-subtitle-2"
          justify="space-between"
        >
          <div>{{ $translate('DELIVERY_FEE') }}</div>
          <div>{{ formatter.format(deliveryPrice) }}</div>
        </v-row>
        <v-row
          v-if="hasPickupPrice"
          class="ma-0 text-subtitle-2"
          justify="space-between"
        >
          <div>{{ $translate('PICKUP_FEE') }}</div>
          <div>{{ formatter.format(pickupPrice) }}</div>
        </v-row>
        <v-row
          v-if="getTotalDiscount !== 0"
          class="ma-0 text-subtitle-2"
          justify="space-between"
        >
          <div>{{ $translate('DISCOUNT') }}</div>
          <div class="red--text">{{ formatter.format(getTotalDiscount) }}</div>
        </v-row>
        <v-row
          v-if="isProfUser"
          class="ma-0 text-subtitle-2"
          justify="space-between"
        >
          <div>{{ $translate('VAT') }}</div>
          <div>{{ formatter.format(getTotalVatPrice) }}</div>
        </v-row>
        <v-row
          v-if="getDepositValue > 0"
          justify="space-between"
          class="ma-0 text-subtitle-2"
        >
          <div>{{ $translate('DEPOSIT_VALUE') }}</div>
          <div>{{ formatter.format(getDepositValue) }}</div>
        </v-row>
        <v-divider
          v-if="getTotalDiscount !== 0 || isDeliveryOrder"
          class="my-2"
        ></v-divider>
        <v-row class="ma-0" justify="space-between">
          <h3>{{ $translate('LINDA_TOTAL_PRICE') }}</h3>
          <h3>{{ formatter.format(getTotalPrice) }}</h3>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="mb-3 mt-2">
      <h2 class="pl-2 my-1 pa-sm-0 primary--text">
        {{ $translate('CONTACT_DETAILS') }}
      </h2>
    </v-row>

    <BookingOverviewOrderData />

    <!-- Payment method -->
    <section v-if="hasMultiPaymentMethods">
      <v-row class="mb-3 mt-5">
        <h2 class="pl-2 my-1 pa-sm-0 primary--text">
          {{ $translate('SELECT_PAYMENT_METHOD') }}
        </h2>
      </v-row>
      <!-- Payment method selection -->
      <v-card flat>
        <PaymentMethodSelect
          v-model="selectedPaymentMethod"
          :items="paymentMethods"
          :ref="paymentMethodSelectElId"
          :id="paymentMethodSelectElId"
        />
      </v-card>
    </section>

    <v-row class="ma-0 mt-10">
      <v-btn
        color="secondary"
        dark
        class="white--text text-capitalize"
        @click="goBack"
      >
        {{ $translate('BACK') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        @click="
          productShoppingCart.length !== 0
            ? reserveOrderEvent()
            : bookWithReservations()
        "
        :loading="paymentBtnLoading"
        :disabled="paymentMethods.length === 0"
        class="primary-font-color text-capitalize"
      >
        {{ getContinueBtnText }}
      </v-btn>
    </v-row>

    <!-- Success dialog -->
    <SuccessDialog
      :dialog="successDialog"
      :successText="paymentMsg"
      @continue="goToMyActivities()"
      @back="goHome()"
    />

    <!-- failed products dialog -->
    <FailedProductsDialog
      :dialog="showErrorDialog"
      :items="failedProducts"
      @cancel="onCancel()"
    ></FailedProductsDialog>
  </v-col>
</template>

<script>
import doRequest from '@/api/dssRequest';
import { mapGetters } from 'vuex';
import BookingProductList from './Booking_Product_List.vue';
import BookingOverviewOrderData from './Booking_Overview_OrderData.vue';
import SuccessDialog from '../generalComponents/uiComponents/SuccessDialog.vue';
import ShoppingCart from '@/objects/ShoppingCart';
import PickupFeeJS from '@/objects/PickupFee';
import PaymentMethodSelect from '@/components/booking/PaymentMethodSelect';
import PaymentMethodObj from '@/objects/PaymentMethod';
import { scrollIntoView } from '@/shared/utility';
import FailedProductsDialog from '../generalComponents/uiComponents/FailedProductsDialog.vue';

export default {
  name: 'BookingOrderOverview',
  props: {
    allOrders: Array,
  },
  components: {
    BookingProductList,
    BookingOverviewOrderData,
    SuccessDialog,
    PaymentMethodSelect,
    FailedProductsDialog,
  },
  data() {
    return {
      products: [],
      deliveryFee: {},
      paymentMsg: '',
      successDialog: false,
      paymentBtnLoading: false,
      password: '',
      rentalOrders: '',
      productShoppingCart: [],
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),

      paymentMethods: [],
      selectedPaymentMethod: undefined,
      paymentMethodSelectElId: 'paymentMethodSelect',

      pickupFee: null,
      showErrorDialog: false,
      failedProducts: [],
    };
  },
  computed: {
    ...mapGetters({
      isProfUser: 'isProfUser',
    }),
    userDetails() {
      return this.$store.getters.getUserDetails;
    },
    orderDetails() {
      return this.$store.getters.getOrderDetails;
    },
    orderType() {
      return this.$store.getters.getOrderType;
    },
    listOfTickets() {
      return this.$store.getters.getListOfTickets;
    },
    recipientUserObject() {
      return this.$store.getters.getRecipientUserObject;
    },
    notes() {
      return this.$store.getters.getNotes;
    },
    userIsLoggedIn() {
      return this.$store.getters['auth/userName'];
    },
    couponFieldsNotEmpty() {
      // check if there is something is filled in
      return (
        (this.recipientUserObject.FirstName != null &&
          this.recipientUserObject.FirstName !== '') ||
        (this.recipientUserObject.LastName != null &&
          this.recipientUserObject.LastName !== '') ||
        (this.recipientUserObject.Email != null &&
          this.recipientUserObject.Email !== '')
      );
    },
    userObjectId() {
      return this.userIsLoggedIn
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    getTotalDiscount() {
      return ShoppingCart.getTotalDiscount(this.allOrders);
    },
    getDepositValue() {
      return ShoppingCart.getDepositValue(this.allOrders);
    },
    getTotalPrice() {
      let orderTotalPrice = 0;

      this.allOrders.forEach((order) => {
        orderTotalPrice += order.OrderTotalPrice;
      });

      if (this.deliveryFee.Price != null) {
        orderTotalPrice += this.deliveryFee.Price;
      }

      Object.values(this.listOfTickets).forEach((ticket) => {
        orderTotalPrice += ticket.Price * ticket.Amount;
      });

      if (this.isPickupOrder && this.hasPickupPrice) {
        orderTotalPrice += this.pickupFee.price;
      }

      return orderTotalPrice;
    },
    getSubtotalPrice() {
      let price = ShoppingCart.getSubtotalPrice(this.allOrders);

      Object.values(this.listOfTickets).forEach((ticket) => {
        price += (ticket.Price - ticket.VatPrice) * ticket.Amount;
      });

      return price;
    },
    getTotalVatPrice() {
      let totalVatPrice = 0;

      this.allOrders.forEach((order) => {
        totalVatPrice += order.OrderTotalPriceVat;
      });

      if (
        this.deliveryFee != null &&
        this.deliveryFee.ResultSet != null &&
        this.deliveryFee.ResultSet.VatPrice != null
      ) {
        totalVatPrice += this.deliveryFee.ResultSet.VatPrice;
      }

      Object.values(this.listOfTickets).forEach((ticket) => {
        totalVatPrice += ticket.VatPrice * ticket.Amount;
      });

      if (this.isPickupOrder && this.hasPickupPrice) {
        totalVatPrice += this.pickupFee.vatPrice;
      }

      return totalVatPrice;
    },
    computedDateFormattedStartDate() {
      return this.formatDate(this.orderDetails.StartDate);
    },
    getContinueBtnText() {
      return this.$translate('PAYMENT_CONFIRM_PAY');
    },
    isDeliveryOrder() {
      return (
        this.orderType != null &&
        (this.orderType.Type === 'Delivery' ||
          this.orderType.Type === 'Shiplee')
      );
    },
    isPickupOrder() {
      return this.orderType != null && this.orderType.Type === 'PickUp';
    },
    isValidDeliveryDetails() {
      if (
        this.deliveryFee != null &&
        this.deliveryFee.ResultSet != null &&
        this.deliveryFee.ResultSet.Price != null
      ) {
        return true;
      }
      return false;
    },
    deliveryPrice() {
      if (!this.isValidDeliveryDetails) {
        return 0;
      }

      if (this.isProfUser) {
        return this.deliveryPriceExclVAT;
      }
      return this.deliveryPriceInclVAT;
    },
    deliveryPriceInclVAT() {
      if (!this.isValidDeliveryDetails) {
        return 0;
      }
      return this.deliveryFee.ResultSet.Price;
    },
    deliveryPriceExclVAT() {
      if (!this.isValidDeliveryDetails) {
        return 0;
      }
      return (
        this.deliveryFee.ResultSet.Price - this.deliveryFee.ResultSet.VatPrice
      );
    },
    hasMultiPaymentMethods() {
      return this.paymentMethods != null && this.paymentMethods.length > 1;
    },
    hasPickupPrice() {
      return this.pickupFee != null && this.pickupFee.hasPrice();
    },
    pickupPrice() {
      if (!this.hasPickupPrice) {
        return 0.0;
      }

      if (this.isProfUser) {
        return this.pickupFee.price - this.pickupFee.vatPrice;
      }
      return this.pickupFee.price;
    },
  },
  mounted() {
    this.getDeliveryDetails();
    this.getPickupFee();
    this.getPaymentMethods();
    this.productShoppingCart = this.$store.getters[
      'shoppingCart/orders'
    ].filter((item) => item.ShoppingCartType === 'Product');
  },
  methods: {
    getDeliveryDetails() {
      this.deliveryFee = {};
      if (!this.isDeliveryOrder) {
        return;
      }

      if (!this.userDetails.postalCode) return;

      const payload = {
        UserInfo: this.getUserInfo(),
        PostalCode: this.userDetails.postalCode,
        Country: this.userDetails.country,
        Reservations: {},
        OneClickBooking: true,
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          payload.Reservations[reservation.EventId] = [reservation.OrderId];
        });
      }

      doRequest(
        (response) => {
          this.deliveryFee = response.data;
        },
        () => {},
        'getDeliveryFee',
        this.orderType.EventId,
        payload
      );
    },
    goHome() {
      this.$store.commit('shoppingCart/setOrders', []);
      this.$router.push({
        name: 'Homepage',
        params: {
          account_id: this.$store.getters.getRegion,
        },
      });
    },
    goToMyActivities() {
      this.$store.commit('shoppingCart/setOrders', []);
      this.$router.push({
        name: 'UserSettings',
        params: { myOrders: true },
      });
    },
    bookWithReservations(
      orderEvent = {},
      ignoreUserPlanboardBoolean = false,
      receivedFunctionName = ''
    ) {
      if (!this.validatePaymentMethod() || this.paymentMethods === null) {
        this.scrollToPaymentMethod();
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }

      const allEventsForOrder = this.getShoppingCartReservations();

      // add extra order to payload if it has products
      if (this.productShoppingCart.length !== 0) {
        allEventsForOrder[orderEvent.EventId] = [orderEvent.OrderId];
      }

      const voucherReceiverInfo = this.couponFieldsNotEmpty
        ? this.recipientUserObject
        : {};

      const payload = {
        OnlySpecialRequests: false,
        IgnoreUserPlanBoard: ignoreUserPlanboardBoolean,
        UserInfo: this.getUserInfo(),
        Note: this.notes ? this.notes : '',
        Reservations: allEventsForOrder || {},
        VoucherReceiverInfo: voucherReceiverInfo,
        PaymentMethod: this.selectedPaymentMethod,
      };

      // to find out what the objectId and functionName are
      let objectId = '';
      let functionName = '';
      if (receivedFunctionName !== '') {
        functionName = receivedFunctionName;
      } else if (this.$store.getters['auth/userName'] !== '') {
        objectId = this.$store.getters['auth/userName'];
        functionName = 'bookWithReservations';
      } else {
        objectId = `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_UM`;
        functionName = 'bookWithReservationsNoLogin';
      }

      // check if the user wants to create an account
      if (this.createAccount) {
        // set register on true
        payload.Register = true;
        // send the password in the call
        payload.Password = this.password;
      }

      this.paymentBtnLoading = true;
      doRequest(
        (response) => {
          this.paymentBtnLoading = false;

          switch (response.data.Status) {
            case 'PartnerConfirmation':
              // Need to wait for partner's response
              console.log('Order needs to be confirmed by partner');
              this.successDialog = true;
              this.paymentMsg = this.$translate(
                'ORDER_WAIT_FOR_PARTNER_CONFIRMATION'
              );
              break;
            case 'Confirmed':
              // Order confirmed by payment method of DirectDebit or free of charge
              console.log('Order already been confirmed');
              if (
                response.data.PaymentMessage != null &&
                response.data.PaymentMessage.trim() !== ''
              ) {
                this.paymentMsg = response.data.PaymentMessage;
              } else {
                this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
              }
              this.successDialog = true;
              break;
            case 'WaitingForPayment':
              // Waiting for payment, redirect to mollie
              console.log('Order waiting for payment');
              if (
                response.data.PaymentUrl != null &&
                response.data.PaymentUrl.trim() !== ''
              ) {
                this.redirectPayment(response.data.PaymentUrl);
              } else {
                window.EventBus.fire(
                  'activateSnackBar',
                  this.$translate('BOOKING_FAIL')
                );
              }
              break;
            default:
              console.log('Unknown order status: ', response.data.Status);
              this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
              this.successDialog = true;
              break;
          }

          if (functionName === 'bookWithReservationsNoLogin') {
            if (response.data.UserId != null && response.data.UserId !== '') {
              this.$store.commit('auth/setTmpUserId', response.data.UserId);
            }
          }
        },
        (error) => {
          this.paymentBtnLoading = false;

          if (functionName === 'bookWithReservationsNoLogin') {
            if (error.UserId != null && error.UserId !== '') {
              this.$store.commit('auth/setTmpUserId', error.UserId);
            }
          }

          if (error.data.ErrorCode === 'E_PO_023') {
            window.EventBus.fire(
              'activateErrorDialog',
              this.$translate('E_PO_023_BOOKWITHRESERVATIONS'),
              false
            );
          } else if (error.data.ErrorCode === 'E_PO_011') {
            window.EventBus.fire(
              'activateErrorDialog',
              this.$translate('E_PO_011'),
              false,
              this.$translate('NO'),
              this.$translate('YES'),
              orderEvent,
              'continueAfterErrorBookingInBookingUserDetails',
              functionName
            );
          } else {
            window.EventBus.fire(
              'activateSnackBar',
              this.$translate('BOOKING_FAIL')
            );
          }
        },
        functionName,
        objectId,
        payload,
        'POST',
        600000,
        false
      );
    },
    redirectPayment(paymentUrl) {
      window.location.href = paymentUrl;
    },
    formatDate(data) {
      if (!data) return '';
      const [year, month, day] = data.split('-');
      return `${day}-${month}-${year}`;
    },
    getUserInfo() {
      return {
        Name:
          `${this.userDetails.firstName} ${this.userDetails.lastName}` || '',
        Email: this.userDetails.email || '',
        PhoneNumber: this.userDetails.phoneNo || '',
        Street: this.userDetails.street || '',
        Nr: this.userDetails.houseNo || '',
        PostalCode: this.userDetails.postalCode || '',
        City: this.userDetails.city || '',
        Country: this.userDetails.country || '',
        CountryCode: this.userDetails.countryCode || '',
        Latitude:
          this.userDetails.latitude == null ? -200 : this.userDetails.latitude,
        Longitude:
          this.userDetails.longitude == null
            ? -200
            : this.userDetails.longitude,
      };
    },
    reserveOrderEvent() {
      const payload = {
        OrderId: '',
        StartTime: this.orderDetails.StartTime.StartTime,
        EndTime: '',
        StartDate: this.computedDateFormattedStartDate,
        EndDate: this.computedDateFormattedEndDate,
        StartBatchGroupId: this.orderDetails.StartTime.BatchGroupId,
        EndBatchGroupId: this.orderDetails.StartTime.BatchGroupId,
        UserInfo: this.getUserInfo(),
        Reservations: this.getShoppingCartReservations(),
        EventId: this.orderType.EventId,
        Tickets: {},
      };

      payload.Note = this.notes;
      payload.SpecialRequests = [];
      for (const item of Object.values(this.listOfTickets)) {
        if (item.Amount > 0) payload.Tickets[item.Id] = item.Amount;
      }

      this.paymentBtnLoading = true;
      doRequest(
        (response) => {
          this.paymentBtnLoading = false;

          const orderEvent = {
            EventId: response.data.EventInfo.EventId,
            OrderId: response.data.MainOrder.OrderId,
          };
          this.bookWithReservations(orderEvent, false);
        },
        (error) => {
          this.paymentBtnLoading = false;

          let errorMsg = '';
          if (error.data.ErrorCode === 'E_DEL_002') {
            errorMsg = this.$translate('E_DEL_002');
          } else if (error.data.ErrorCode === 'E_ORD_001') {
            errorMsg = this.$translate('TICKETS_FAIL_RESERVED');
            this.showErrorDialog = true;
            this.failedProducts = error.data.FailedProducts;
          } else {
            errorMsg = this.$translate('TICKETS_FAIL_RESERVED');
          }

          window.EventBus.fire('activateSnackBar', {
            text: errorMsg,
            color: 'error',
          });
        },
        'reserve',
        this.userObjectId,
        payload
      );
    },
    goBack() {
      this.$emit('back');
    },
    getTicketUnitPrice(ticket) {
      if (this.isProfUser) {
        return ticket.Price - ticket.VatPrice;
      }
      return ticket.Price;
    },
    getTicketTotalPrice(ticket) {
      return this.getTicketUnitPrice(ticket) * ticket.Amount;
    },
    getShoppingCartReservations() {
      const cartOrders = this.$store.getters['shoppingCart/orders'];
      const reservations = {};

      if (cartOrders != null) {
        cartOrders.forEach((reservation) => {
          if (reservations[reservation.EventId] == null) {
            reservations[reservation.EventId] = [];
          }

          if (
            !reservations[reservation.EventId].includes(reservation.OrderId)
          ) {
            reservations[reservation.EventId].push(reservation.OrderId);
          }
        });
      }

      return reservations;
    },
    getPaymentMethods() {
      const deliveryPickupEventId = this.orderType.EventId;
      const reservations = this.getShoppingCartReservations();
      PaymentMethodObj.getPaymentMethods(
        this.userObjectId,
        reservations,
        deliveryPickupEventId
      )
        .then((methods) => {
          this.paymentMethods = [...methods];
          if (this.paymentMethods.length === 1) {
            [this.selectedPaymentMethod] = this.paymentMethods;
          }
        })
        .catch(() => {
          console.log('An error occurred in getPaymentMethods');
          this.paymentMethods = [];
        });
    },
    validatePaymentMethod() {
      const paymentRef = this.$refs[this.paymentMethodSelectElId];
      if (paymentRef != null && !paymentRef.validate()) {
        return false;
      }

      return true;
    },
    scrollToPaymentMethod() {
      scrollIntoView(`#${this.paymentMethodSelectElId}`);
    },
    getPickupFee() {
      if (!this.isPickupOrder) {
        this.pickupFee = null;
        return;
      }

      this.pickupFee = new PickupFeeJS(this.orderType.EventId);
      this.pickupFee.load(this.getShoppingCartReservations());
    },
    onCancel() {
      this.showErrorDialog = false;
    },
  },
};
</script>
